// Навигационные ссылки клиентского портала — общие для desktop sidebar
// и mobile drawer (D09 §1). Один источник правды для пунктов меню,
// иначе drawer и sidebar разойдутся при добавлении/переименовании пунктов.
import {
  Package,
  ClipboardList,
  ShoppingCart,
  User,
  Database,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { Link } from '../lib/router'
import { usePortalSession } from '../features/invitations/usePortalMembership'

export type ClientNavActive =
  | 'catalog'
  | 'cart'
  | 'orders'
  | 'data'
  | 'profile'
  | 'employees'

export interface ClientNavLinksProps {
  active: ClientNavActive
  /** Колбэк после клика по ссылке (например, закрыть mobile drawer). */
  onNavigate?: () => void
}

interface NavItem {
  key: ClientNavActive
  to: string
  label: string
  icon: LucideIcon
  testId?: string
  permission: string
}

const ITEMS: ReadonlyArray<NavItem> = [
  { key: 'catalog', to: '/catalog', label: 'Каталог', icon: Package, permission: 'client.catalog.view' },
  {
    key: 'cart',
    to: '/cart',
    label: 'Корзина',
    icon: ShoppingCart,
    testId: 'client-side-nav-cart',
    permission: 'client.cart.use',
  },
  {
    key: 'orders',
    to: '/orders',
    label: 'Заказы',
    icon: ClipboardList,
    testId: 'client-side-nav-orders',
    permission: 'client.orders.view',
  },
  {
    key: 'data',
    to: '/me',
    label: 'Мои точки и доступ',
    icon: Database,
    testId: 'client-side-nav-data',
    permission: 'client.tradepoints.view',
  },
  {
    key: 'profile',
    to: '/profile',
    label: 'Профиль',
    icon: User,
    testId: 'client-side-nav-profile',
    permission: 'user.self.view',
  },
]

export function ClientNavLinks({ active, onNavigate }: ClientNavLinksProps) {
  const session = usePortalSession()
  if (session.isError || session.data?.state !== 'ACTIVE'
    || !['CLIENT_OWNER', 'CLIENT_EMPLOYEE'].includes(session.data.role)) return null
  const permissions = session.data.permissions
  return (
    <>
      {ITEMS.filter((item) => item.key === 'data' || permissions.includes(item.permission)).map(({ key, to, label, icon: Icon, testId }) => (
        <Link
          key={key}
          to={to}
          className={`client-side-nav-item${
            active === key ? ' client-side-nav-item--active' : ''
          }`}
          aria-current={active === key ? 'page' : undefined}
          data-testid={testId}
          onClick={onNavigate ? () => onNavigate() : undefined}
        >
          <Icon size={16} aria-hidden="true" />
          {label}
        </Link>
      ))}
      {session.data.role === 'CLIENT_OWNER'
        && permissions.some(code => ['client.users.manage', 'invitation.create'].includes(code))
        && <Link to="/group-employees" onClick={onNavigate}
          className={`client-side-nav-item${active === 'employees' ? ' client-side-nav-item--active' : ''}`}
          aria-current={active === 'employees' ? 'page' : undefined}>
          <Users size={16} aria-hidden="true" />Сотрудники
        </Link>}
    </>
  )
}
